import {
  ICustomField,
  IShow,
  IStand,
  IStandToCustomField,
} from "@evennt-tech/api-v2-core";
import { getStandSizeValue } from "view/apps/Organiser/pages/Show/Exhibitor/Settings/components/SizeSettingRow";
import formatDate from "tools/formats/formatDate";
import { getReadableString } from "constraints/general";
import { IGetPublicInfoShowRes } from "@evennt-tech/api-v2-external";
import { Locale } from "date-fns";
import {
  buildUnresolvedVariableHtml,
  UNRESOLVED_VARIABLE_HTML,
} from "tools/task/unresolvedVariable";

export type ShowWithVariables = Pick<
  IGetPublicInfoShowRes,
  | "venueName"
  | "liveStart"
  | "liveEnd"
  | "name"
  | "id"
  | "venueAddress1"
  | "venueCountry"
  | "venueCity"
  | "postcode"
  | "contactName"
  | "contactEmail"
  | "contactPhoneNumber"
  | "fax"
  | "unitSystem"
  | "dateFormat"
>;

/**
 * Loose `task` shape — only `stand` and `standToCustomFields` are read here.
 */
export type TaskWithStandContext = {
  stand?: IStand;
  standToCustomFields?: IStandToCustomField[];
};

export const generateDefaultVariables = (
  task?: TaskWithStandContext,
  show?: ShowWithVariables,
  locale?: Locale,
  unresolved: string = UNRESOLVED_VARIABLE_HTML
): Record<string, string> => ({
  "Stand Number": task?.stand?.name || unresolved,
  "Stand Size":
    task?.stand?.size?.length && task?.stand?.size?.width && show
      ? getStandSizeValue(task?.stand?.size, show.unitSystem)
      : unresolved,
  "Open Sides":
    task?.stand?.openSides != null
      ? task.stand.openSides.toString()
      : unresolved,
  "Wall Length":
    task?.stand?.standWallLength != null
      ? task.stand.standWallLength.toString()
      : unresolved,
  "Build Type": task?.stand?.builder?.name || unresolved,
  "Venue name": show?.venueName || unresolved,
  "Show start date": show
    ? formatDate(new Date(show.liveStart), getReadableString(show), locale)
    : unresolved,
  "Show end date": show
    ? formatDate(new Date(show.liveEnd), getReadableString(show), locale)
    : unresolved,
  "Show name": show?.name || unresolved,
  "Venue Address": show?.venueAddress1 || unresolved,
  "Venue City": show?.venueCity || unresolved,
  "Venue Country": show?.venueCountry?.name || unresolved,
  Postcode: show?.postcode || unresolved,
  "Contact Name": show?.contactName || unresolved,
  "Contact phone number": show?.contactPhoneNumber || unresolved,
  "Contact email": show?.contactEmail || unresolved,
  "Additional phone number/Fax": show?.fax || unresolved,
});

/**
 * The concrete rendered value of a custom field on this Show's stand, or
 * `null` when the field has no value entered.
 */
const getCustomFieldRenderedValue = (
  customField: ICustomField,
  task?: TaskWithStandContext
): string | null => {
  const standCustomField = (task?.standToCustomFields || []).find(
    ({ customFieldId }) => customField.id === customFieldId
  );

  if (!standCustomField) {
    return null;
  }

  const {
    hyperlink,
    customFieldToFiles,
    name,
    text,
    number,
    paragraph,
  } = standCustomField;

  if (hyperlink) {
    const safeHyperlink = hyperlink.replace(/&/g, "&amp;");
    return `<a href="${safeHyperlink}" style="text-decoration: none;" target="_blank">${
      name || safeHyperlink
    }</a>`;
  }
  if (customFieldToFiles && customFieldToFiles.length > 0) {
    return customFieldToFiles
      .map(
        (customFieldToFile) =>
          `<a href="${
            customFieldToFile!.file!.url
          }" target="_blank" style="text-decoration: none;">${
            customFieldToFile!.file!.title
          }</a>`
      )
      .join(" ");
  }
  if (text || number?.toString() || paragraph) {
    return text || number?.toString() || paragraph || null;
  }
  return null;
};

type GetCustomFieldsMapParams = {
  task?: TaskWithStandContext;
  show?: ShowWithVariables;
  customFields?: ICustomField[];
  locale?: Locale;
  unresolved?: string;
};
export const getCustomFieldsMap = ({
  task,
  show,
  customFields = [],
  locale,
  unresolved = UNRESOLVED_VARIABLE_HTML,
}: GetCustomFieldsMapParams) => {
  const defaultVariables = generateDefaultVariables(
    task,
    show,
    locale,
    unresolved
  );

  if (customFields.length) {
    const customFieldVariables: Record<string, string> = {};

    customFields.forEach((customField) => {
      // The token in a description is keyed by the ENGLISH title, always — so
      // a localised read has to match on `sourceTitle`, not the substituted
      // `title`, or the placeholder stops matching and gets stripped.
      const title = customField.sourceTitle ?? customField.title;
      customFieldVariables[title] =
        getCustomFieldRenderedValue(customField, task) ?? unresolved;
    });

    return {
      ...defaultVariables,
      ...customFieldVariables,
    };
  }

  return defaultVariables;
};

export const decodeHtmlEntities = (str: string): string => {
  const htmlEntities: { [key: string]: string } = {
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    // eslint-disable-next-line quotes
    "&quot;": '"',
    "&#39;": "'",
    "&nbsp;": " ",
  };

  return Object.entries(htmlEntities).reduce((result, [key, value]) => {
    return result.replace(new RegExp(key, "g"), value);
  }, str);
};

export const replaceLineBreaksWithNewlines = (html: string): string => {
  if (!html) {
    return "";
  }
  // Capture <a> tags with hrefs and replace with a placeholder
  const anchors: Record<string, string>[] = [];
  html = html.replace(
    /<a[^>]+href="([^"]+)"[^>]*>([^<]+)<\/a>/g,
    (match, href, content) => {
      anchors.push({ href, content });
      return `LINK_PLACEHOLDER_${anchors.length - 1}`;
    }
  );

  // Remove all other HTML tags and entities
  html = html
    .replace(/<\/p>/g, "\n")
    .replace(/<p>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");

  // Restore the <a> tags with hrefs
  anchors.forEach((anchor, index) => {
    html = html.replace(
      `LINK_PLACEHOLDER_${index}`,
      `<a href="${anchor.href}">${anchor.content}</a>`
    );
  });

  return html;
};

type GetTextWithVariables = {
  task?: TaskWithStandContext;
  show?: ShowWithVariables | null;
  customFields?: ICustomField[];
  description: string;
  contrastColor?: string;
  locale?: Locale;
  /** Text of the unresolved-variable placeholder; English when omitted. */
  unresolvedLabel?: string;
};

// The editor binds a custom-field token to its field by `data-cf-id` on the
// token's outermost inline element — a span, or strong / em / a / … when the
// token is formatted. Older content let that binding run on over the text
// typed after the token, even onto later lines, so only a token that opens a
// bound element is the field's; any other is left to the title pass.
const FIRST_TOKEN = /<[^<>]+>/;

const resolveBoundTokens = (
  html: string,
  valueOf: (customFieldId: string) => string
): string => {
  if (!html.includes("data-cf-id")) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode as Text);

  const settledRuns = new Set<Element>();
  let changed = false;
  textNodes.forEach((textNode) => {
    const run = textNode.parentElement?.closest("[data-cf-id]");
    const id = run?.getAttribute("data-cf-id");
    if (!run || !id || settledRuns.has(run)) return;

    const match = FIRST_TOKEN.exec(textNode.data);
    const lead = match ? textNode.data.slice(0, match.index) : textNode.data;
    if (lead.trim()) {
      settledRuns.add(run);
      return;
    }
    if (!match) return;
    settledRuns.add(run);

    const rest = textNode.splitText(match.index);
    rest.data = rest.data.slice(match[0].length);
    const value = doc.createElement("template");
    value.innerHTML = valueOf(id);
    rest.before(value.content);
    changed = true;
  });
  return changed ? doc.body.innerHTML : html;
};

const VARIABLE_COLOR = "rgb(41, 121, 255)";
const VARIABLE_TOKEN = /<[^<>]+>/;

/**
 * The editor paints variable tokens #2979FF; this swaps that for `color` on
 * render. Users can pick #2979FF for any text or background too, so only the
 * `color` of an element holding a `<Variable>` token is replaced. Text typed
 * right after a variable shares its span, so the token need not be alone.
 */
export const recolorVariables = (html: string, color: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  let changed = false;
  doc.body.querySelectorAll<HTMLElement>("[style]").forEach((element) => {
    if (
      element.style.color === VARIABLE_COLOR &&
      VARIABLE_TOKEN.test(element.textContent ?? "")
    ) {
      element.style.color = color;
      changed = true;
    }
  });
  return changed ? doc.body.innerHTML : html;
};

const getTextWithVariables = ({
  task,
  show,
  customFields,
  description,
  contrastColor,
  locale,
  unresolvedLabel,
}: GetTextWithVariables): string => {
  if (!show) {
    return "";
  }
  const unresolved = buildUnresolvedVariableHtml(unresolvedLabel);
  const variableMapping = getCustomFieldsMap({
    task,
    show,
    customFields,
    locale,
    unresolved,
  });

  // Id-bound custom-field tokens FIRST — a field bound to a specific Show only
  // resolves when it belongs to the Show being rendered (`showId ===
  // currentShowId`); a same-name field on another Show is a different id and
  // stays unresolved. Runs before the generic pass so the inner `‹Title›` is
  // consumed here rather than by the legacy title match.
  let result = description
    ? recolorVariables(description, contrastColor || "rgb(0, 0, 0)")
    : description;

  result =
    result &&
    resolveBoundTokens(result, (id) => {
      const customField = (customFields || []).find((field) => field.id === id);
      if (!customField || customField.showId !== show.id) {
        return unresolved;
      }
      return getCustomFieldRenderedValue(customField, task) ?? unresolved;
    });

  // Legacy title-only tokens (no id) — existing tasks keep resolving by title.
  const regex = /&lt;([\s\S]*?)&gt;/g;
  result = result?.replace(regex, (match, content) => {
    const decodedContent = decodeHtmlEntities(content);
    return variableMapping[decodedContent] || match;
  });
  return result;
};

export default getTextWithVariables;
